import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import i18next from "src/i18n/config";

import { AddDeviceModal } from "@dashboard/devices/components/Table/AddDeviceModal/AddDeviceModal";
import { useReleaseFiles } from "@dashboard/downloads/hooks/useReleaseFiles";
import { ActivateDeviceStep } from "@dashboard/enrollment/components/ActivateDeviceStep";
import { DownloadImageStep } from "@dashboard/enrollment/components/DownloadImageStep";
import { EnrollmentTypeSelectionStep } from "@dashboard/enrollment/components/EnrollmentTypeSelectionStep";
import { SelectDeviceTypeStep } from "@dashboard/enrollment/components/SelectDeviceTypeStep";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import GenerateQRCodeDialogWrapper from "@dashboard/shared/components/SidePanel/GroupPanel/EnrollmentTab/qrCodeDialog/GenerateQRCodeDialogWrapper";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";
import { showErrorToast } from "@shared/toasts/Toasts";

import { codenameParam, resolveWizardStep, toWizardParams, versionParam } from "./EnrollmentPage.utils";

export type EnrollmentPath = "install_android_os" | "install_mdm" | "enroll_existing" | "build_custom";

type StepProps = {
    onSkip: () => void;
    onBack: () => void;
    onNext: () => void;
    data: {
        releaseFile: API.ReleaseFile | null;
        releaseFiles: ReturnType<typeof useReleaseFiles>;
        chosenCodename: string | null;
        chosenVersion: string | null;
        onReleaseFileSelect: (releaseFile: API.ReleaseFile) => void;
        onGoToDevices: () => void;
    };
};

type EnrollmentStep = {
    key: string;
    requiresReleaseFile?: boolean;
    render: (props: StepProps) => React.ReactNode;
};

type EnrollmentFlow = EnrollmentStep[];

type EnrollmentFlows = Record<"install_android_os", EnrollmentFlow>;

// The step keys are part of the page URL (`?step=download-flash`), so renaming one breaks existing links to it.
const flows: EnrollmentFlows = {
    install_android_os: [
        {
            key: "image-selection",
            render: ({ onSkip, onBack, data }) => (
                <SelectDeviceTypeStep
                    onSkip={onSkip}
                    onBack={onBack}
                    releaseFiles={data.releaseFiles}
                    initialCodename={data.chosenCodename}
                    initialVersion={data.chosenVersion}
                    onContinue={data.onReleaseFileSelect}
                />
            ),
        },
        {
            key: "download-flash",
            requiresReleaseFile: true,
            render: ({ onSkip, onBack, onNext, data }) => (
                <DownloadImageStep onSkip={onSkip} onBack={onBack} onContinue={onNext} selectedReleaseFile={data.releaseFile} />
            ),
        },
        {
            key: "activate-device",
            render: ({ onSkip, onBack, data }) => (
                <ActivateDeviceStep onSkip={onSkip} onBack={onBack} onContinue={data.onGoToDevices} />
            ),
        },
    ],
};

// Marks a history entry the wizard pushed itself, so its Back button can pop that entry instead of pushing another one.
type WizardHistoryState = {
    fromWizard?: boolean;
};

const EnrollmentPage = () => {
    const [openDialog, setOpenDialog] = useState<"sixTaps" | "register" | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { t } = useTranslation(["modals"]);
    const navigate = useNavigate();
    const { data: workspace } = useCurrentWorkspace();
    const releaseFiles = useReleaseFiles();

    const chosenCodename = searchParams.get(codenameParam);
    const chosenVersion = searchParams.get(versionParam);
    const wizardStep = resolveWizardStep(flows, searchParams, releaseFiles);
    const current = wizardStep.kind === "step" ? wizardStep : null;
    const redirectSearch = wizardStep.kind === "redirect" ? wizardStep.search : null;

    const goTo = (params: URLSearchParams) => {
        setSearchParams(params, { state: { fromWizard: true } satisfies WizardHistoryState });
    };

    const handleCancel = () => {
        goTo(new URLSearchParams());
    };

    const handleBack = () => {
        if ((location.state as WizardHistoryState | null)?.fromWizard) {
            void navigate(-1);
            return;
        }
        const previousStepKey = current && current.index > 0 ? current.flow[current.index - 1].key : null;
        setSearchParams(toWizardParams(previousStepKey, searchParams), { replace: true });
    };

    const handleContinue = () => {
        const nextStep = current?.flow[current.index + 1];
        if (nextStep) {
            goTo(toWizardParams(nextStep.key, searchParams));
        }
    };

    const handlePathSelect = (path: EnrollmentPath) => {
        switch (path) {
            case "install_android_os":
                goTo(toWizardParams(flows.install_android_os[0].key, new URLSearchParams()));
                return;
            case "install_mdm":
                if (!workspace?.defaultGroupId) {
                    showErrorToast(t("modals:enrollmentDialog.enrollFirstDevice.cards.installMdm.noDefaultGroup"));
                    return;
                }
                setOpenDialog("sixTaps");
                return;
            case "enroll_existing":
                setOpenDialog("register");
                return;
            case "build_custom":
                window.open("https://emteria.com/lp/demo", "_blank");
        }
    };

    const handleReleaseFileSelect = (selectedFile: API.ReleaseFile) => {
        const nextStep = current?.flow[current.index + 1];
        if (nextStep) {
            const releaseFileParams = new URLSearchParams({
                [codenameParam]: selectedFile.codename,
                [versionParam]: selectedFile.version,
            });
            goTo(toWizardParams(nextStep.key, releaseFileParams));
        }
    };

    const handleGoToDevices = () => {
        navigate("/dashboard/devices");
    };

    const renderStep = () => {
        if (wizardStep.kind === "type-selection") {
            return <EnrollmentTypeSelectionStep onSelect={handlePathSelect} />;
        }

        if (!current) {
            return <PageContainer isLoading />;
        }

        return current.step.render({
            onSkip: handleCancel,
            onBack: handleBack,
            onNext: handleContinue,
            data: {
                releaseFile: current.releaseFile,
                releaseFiles,
                chosenCodename,
                chosenVersion,
                onReleaseFileSelect: handleReleaseFileSelect,
                onGoToDevices: handleGoToDevices,
            },
        });
    };

    useEffect(() => {
        if (redirectSearch !== null) {
            setSearchParams(redirectSearch, { replace: true });
        }
    }, [redirectSearch, setSearchParams]);

    return (
        <>
            <ExpiredWrapper>{renderStep()}</ExpiredWrapper>

            {workspace?.defaultGroupId ? (
                <GenerateQRCodeDialogWrapper
                    groupId={workspace.defaultGroupId}
                    isOpen={openDialog === "sixTaps"}
                    onClose={() => setOpenDialog(null)}
                />
            ) : null}
            <AddDeviceModal open={openDialog === "register"} onClose={() => setOpenDialog(null)} />
        </>
    );
};

export default withMetaDescription(EnrollmentPage, {
    title: i18next.t("pages:enrollmentPage.metaTitle"),
});
